// src/frontend/js/modern/shared/scope-helpers.js

/**
 * Scope Helper
 * Generates unique conflict scopes for each operation to prevent cross-module/cross-list cancellations.
 */
export const ScopeHelper = {
	/**
	 * Wishlist scope
	 * @param {number|string} productId
	 * @param {number|string|null} listId
	 * @param {boolean} isMerge
	 * @returns {string}
	 */
	wishlist(productId, listId = null, isMerge = false) {
		if (isMerge && listId && listId !== '0' && listId !== 0) {
			return `wishlist::merge::${productId}::${listId}`;
		}
		return `wishlist::product::${productId}`;
	},

	/**
	 * Multi-list scope
	 * @param {number|string} productId
	 * @param {number|string} listId
	 * @returns {string}
	 */
	multiList(productId, listId) {
		return `multilist::${productId}::${listId}`;
	},

	/**
	 * Multi-list batch scope (when adding to multiple lists at once)
	 * @param {number|string} productId
	 * @returns {string}
	 */
	multiListBatch(productId) {
		return `multilist::batch::${productId}`;
	},

	/**
	 * Multi-list management scope — create/update/delete/privacy
	 * @param {string} operation - create, update, delete, privacy
	 * @param {number|string|null} listId
	 * @returns {string}
	 */
	multiListManage(operation, listId = null) {
		if (operation === 'create') {
			return `multilist::manage::create::${listId}`;
		}
		return `multilist::manage::${operation}::${listId}`;
	},

	/**
	 * Multi-list move scope
	 * @param {number|string} productId
	 * @param {number|string} sourceListId
	 * @param {number|string} destListId
	 * @returns {string}
	 */
	multiListMove(productId, sourceListId, destListId) {
		return `multilist::move::${productId}::${sourceListId}::${destListId}`;
	},

	/**
	 * Multi-list copy scope
	 * @param {number|string} productId
	 * @param {number|string} sourceListId
	 * @param {number|string} destListId
	 * @returns {string}
	 */
	multiListCopy(productId, sourceListId, destListId) {
		return `multilist::copy::${productId}::${sourceListId}::${destListId}`;
	},

	/**
	 * Waitlist scope
	 * @param {number|string} productId
	 * @returns {string}
	 */
	waitlist(productId) {
		return `waitlist::product::${productId}`;
	},

	/**
	 * Save-for-Later scope
	 * Uses cart_item_key as primary identifier because same product can have multiple cart items.
	 * @param {string|null} cartItemKey
	 * @param {number|string|null} productId
	 * @returns {string}
	 */
	sfl(cartItemKey, productId = null) {
		if (cartItemKey) return `sfl::cart::${cartItemKey}`;
		if (productId) return `sfl::product::${productId}`;
		return `sfl::unknown::${Date.now()}`;
	},

	/**
	 * SFL item scope — for remove/add-to-cart by item_id
	 * @param {number|string} itemId
	 * @returns {string}
	 */
	sflItem(itemId) {
		return `sfl::item::${itemId}`;
	},

	/**
	 * Save-for-Later batch scope
	 * @returns {string}
	 */
	sflBatch() {
		return `sfl::batch`;
	},

	/**
	 * Generic scope for any other operation
	 * @param {string} module
	 * @param {string} operation
	 * @param {number|string} id
	 * @returns {string}
	 */
	generic(module, operation, id) {
		return `${module}::${operation}::${id}`;
	},
};

export default ScopeHelper;
